import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useT } from '../../i18n'
import type { TranslationKey } from '../../i18n/en'
import { getCurrentOperatorActorId } from '../../utils/operatorSession'
import {
  weightSampleLogApi,
  type WeightSampleLogMutationResult
} from '../../utils/weightSampleLogApi'
import type { WeightSampleLog } from '../../types/production'
import { samplingPlanForLot } from '../../utils/unitPlans'
import OperatorGate from './OperatorGate'
import CrewModeFarmBar from './CrewModeFarmBar'
import { useCrewModeFarm } from './useCrewModeFarm'
import { isWeightSampleCrewModeEnabled } from './weightSampleLogFeatureFlag'
import { recordCrewEvent } from './crewModeTelemetry'
import { ensureTodayProductionRow } from './ensureTodayProductionRow'
import {
  LENGTH_MM_MAX,
  WEIGHT_GRAMS_MAX,
  buildWeightSampleCaptureCreatePayload,
  findLikelyDuplicateWeightSampleLog,
  validateLengthMmValue,
  validateSampleCountValue,
  validateWeightGramsValue
} from './weightSampleCaptureValidation'
import { useCrewLocation } from './useCrewLocation'
import { CrewLocationHeader, CrewLocationPicker, CrewNextLocationButton } from './CrewLocationPicker'
import { buildOperatorUndoAudit } from './crewCaptureHelpers'
import { clearStoredRound, readStoredRound, writeStoredRound, type StoredRoundEntry } from './weightSampleRound'
import { isUnsyncedBadgeState, useRecheckWhileUnsynced } from './useRecheckWhileUnsynced'

// WeightSample v0 PRD — Stage 4 (mean-of-N / single-reading mode, unchanged
// below). PRD `weightsample-individual-length-v1.md` — Stage 4 (PR 2/3)
// adds a second, additive capture mode: "one fish at a time." Charter U-2
// interaction budget for that mode: session setup is the same 3-4 taps as
// v0 (pond chip → [lot sub-select]); each individual reading after that is
// 2 taps (weight + Add) or 3 (weight + length + Add), with no
// re-navigation of the pond/lot picker between fish — see PRD §6. Charter
// O-1/O-2/O-3: no CV, Fulton's K, mean, standing biomass, or any
// multi-reading derived figure is shown or computed anywhere in this
// screen, in either mode — the session tally and sync rollup are plain
// counts of the operator's own taps and sync states, not derived
// biological/economic indicators (PRD §3 items 2, 7 / §4). Every
// individual-mode row is written with `sampleCount: 1` (PRD §5) — this is
// what lets the Reports-side CV/K dataset (PR 3/3) tell an individual
// reading apart from a legacy mean-of-N row by value alone, with no new
// metadata flag. Charter D-4: capturedByActorId is stamped per row, not
// once per session, so an operator handoff mid-round never leaves a later
// reading misattributed.

type SyncBadgeState = 'local' | 'pending' | 'synced' | 'error'
type CaptureMode = 'individual' | 'mean'

const chipButtonStyle: React.CSSProperties = {
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
  padding: '8px 12px',
  width: '100%',
  textAlign: 'left'
}

const modeButtonStyle = (active: boolean): React.CSSProperties => ({
  minHeight: 48,
  borderRadius: 12,
  border: active ? '2px solid #1d4ed8' : '1px solid #d1d5db',
  backgroundColor: active ? '#eff6ff' : 'white',
  fontWeight: 800,
  fontSize: 13,
  cursor: 'pointer',
  padding: '8px 12px',
  flex: 1
})

const primaryButtonStyle: React.CSSProperties = {
  minHeight: 56,
  borderRadius: 12,
  border: '1px solid #1d4ed8',
  backgroundColor: '#1d4ed8',
  color: 'white',
  fontWeight: 800,
  fontSize: 16,
  cursor: 'pointer',
  width: '100%'
}

// PRD §6: "Add" is the highest-tap-frequency control in Crew Mode (tapped
// once per fish, not once per visit), sized larger than any other single
// control shipped so far.
const addButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  minHeight: 64
}

const secondaryButtonStyle: React.CSSProperties = {
  minHeight: 48,
  borderRadius: 12,
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  color: '#374151',
  fontWeight: 800,
  fontSize: 14,
  cursor: 'pointer',
  width: '100%'
}

const finishButtonStyle: React.CSSProperties = {
  ...secondaryButtonStyle,
  minHeight: 56
}

const numericInputStyle = (hasError: boolean): React.CSSProperties => ({
  width: '100%',
  minHeight: 56,
  padding: '8px 14px',
  borderRadius: 10,
  border: hasError ? '2px solid #dc2626' : '1px solid #d1d5db',
  fontSize: 24,
  fontWeight: 700
})

const syncBadgeStyle: Record<SyncBadgeState, React.CSSProperties> = {
  local: { backgroundColor: '#f3f4f6', color: '#374151' },
  pending: { backgroundColor: '#fffbeb', color: '#92400e' },
  synced: { backgroundColor: '#f0fdf4', color: '#166534' },
  error: { backgroundColor: '#fef2f2', color: '#991b1b' }
}

const syncBadgeLabelKey: Record<SyncBadgeState, TranslationKey> = {
  local: 'crewMode.weightSample.sync.local',
  pending: 'crewMode.weightSample.sync.pending',
  synced: 'crewMode.weightSample.sync.synced',
  error: 'crewMode.weightSample.sync.error'
}

const toSyncBadgeState = (syncState: WeightSampleLogMutationResult['syncState']): SyncBadgeState => (
  syncState === 'synced' ? 'synced' : syncState === 'pending' ? 'pending' : 'local'
)

type LastCaptureStatus = {
  weightSampleLogId: string
  weightLabel: string
  sampleCountLabel: string | null
  state: SyncBadgeState
}

type SessionEntry = StoredRoundEntry

export default function WeightSampleCapture() {
  const t = useT()
  const { farms, farmId, selectFarm } = useCrewModeFarm()
  const [mode, setMode] = useState<CaptureMode>('individual')
  const [weightDraft, setWeightDraft] = useState('')
  const [lengthDraft, setLengthDraft] = useState('')
  const [sampleCountDraft, setSampleCountDraft] = useState('')
  const [weightError, setWeightError] = useState<string | null>(null)
  const [lengthError, setLengthError] = useState<string | null>(null)
  const [sampleCountError, setSampleCountError] = useState<string | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState<WeightSampleLog | null>(null)
  const [existingLogsForLot, setExistingLogsForLot] = useState<WeightSampleLog[]>([])
  const [lastCapture, setLastCapture] = useState<LastCaptureStatus | null>(null)
  const [sessionEntries, setSessionEntries] = useState<SessionEntry[]>([])

  // Field Bundle v0 §3 items 1–4: one shared location ("Lot · Unidad").
  const crewLocation = useCrewLocation({ stream: 'weightSample', farmId, farms })
  const { location } = crewLocation
  const productionUnitId = location?.productionUnitId ?? ''
  const selectedLotId = location?.lotId ?? ''
  const selectedLot = location?.lotId ? { id: location.lotId, lotKey: location.lotKey ?? '' } : undefined
  const operatorActorId = getCurrentOperatorActorId() ?? ''
  const weightInputRef = useRef<HTMLInputElement | null>(null)
  const lengthInputRef = useRef<HTMLInputElement | null>(null)
  const sampleCountInputRef = useRef<HTMLInputElement | null>(null)

  // Office Plans v0 §3.3: the tank's planned sampling (Editar instalación).
  const samplingPlan = useMemo(() => {
    const unit = farms.find((farm) => farm.id === farmId)?.productionUnits?.find((candidate) => candidate.id === productionUnitId)
    const unitLotIds = crewLocation.options
      .filter((option) => option.productionUnitId === productionUnitId && option.lotId)
      .map((option) => String(option.lotId))
    return samplingPlanForLot(unit, selectedLotId, unitLotIds)
  }, [farms, farmId, productionUnitId, selectedLotId, crewLocation.options])
  // The length box shows unless a plan says length is not measured.
  const showLength = mode === 'individual' && (!samplingPlan || samplingPlan.measureLength)

  useEffect(() => {
    if (samplingPlan) setMode('individual')
  }, [samplingPlan])

  useEffect(() => {
    if (!farmId) return
    recordCrewEvent('capture.start', { stream: 'weightSample', farmId })
  }, [farmId])

  // Field Bundle v0 §3 item 7: the round lives on the device per operator and
  // lot until "Terminar ronda" — leaving the screen does not lose it.
  useEffect(() => {
    setSessionEntries(selectedLotId ? readStoredRound({ farmId, lotId: selectedLotId, operatorActorId }) : [])
    setWeightDraft('')
    setLengthDraft('')
    if (selectedLotId) requestAnimationFrame(() => weightInputRef.current?.focus())
  }, [farmId, selectedLotId, operatorActorId])

  const updateRound = (update: (prev: SessionEntry[]) => SessionEntry[]) => {
    setSessionEntries((prev) => {
      const next = update(prev)
      if (selectedLotId) writeStoredRound({ farmId, lotId: selectedLotId, operatorActorId }, next)
      return next
    })
  }

  useEffect(() => {
    if (!selectedLotId) {
      setExistingLogsForLot([])
      return
    }
    let cancelled = false
    void weightSampleLogApi.list({ farmId, lotId: selectedLotId }, { preferLocal: true }).then((logs) => {
      if (cancelled) return
      setExistingLogsForLot(logs)
    })
    return () => {
      cancelled = true
    }
  }, [farmId, selectedLotId])

  const resetConfirmationState = () => {
    setWeightError(null)
    setLengthError(null)
    setSampleCountError(null)
    setDuplicateWarning(null)
  }

  const handleConfirmMean = async () => {
    if (!farmId || !productionUnitId || !selectedLotId) return

    // Range validation mirrors the server contract and BLOCKS Confirm on an
    // out-of-range value (WaterLog pattern) — a value the server would
    // reject with a 400 must not be queued as if it saved. This differs
    // from Feed/Mortality's "confirm anyway" soft threshold because
    // weightGrams / sampleCount have hard server bounds. No lot state is
    // read (Charter O-1/O-2/O-3).
    const weightValidation = validateWeightGramsValue(weightDraft)
    if (!weightValidation.ok) {
      setWeightError(
        weightValidation.reason === 'invalid'
          ? t('crewMode.weightSample.error.invalidWeight')
          : t('crewMode.weightSample.error.outOfRangeWeight', { max: String(WEIGHT_GRAMS_MAX) })
      )
      return
    }
    setWeightError(null)

    const sampleCountValidation = validateSampleCountValue(sampleCountDraft)
    if (!sampleCountValidation.ok) {
      setSampleCountError(
        sampleCountValidation.reason === 'invalid'
          ? t('crewMode.weightSample.error.invalidSampleCount')
          : t('crewMode.weightSample.error.outOfRangeSampleCount')
      )
      return
    }
    setSampleCountError(null)

    const recordedAtIso = new Date().toISOString()
    const duplicate = findLikelyDuplicateWeightSampleLog({
      lotId: selectedLotId,
      recordedAtIso,
      existingLogs: existingLogsForLot
    })
    if (duplicate && duplicateWarning?.id !== duplicate.id) {
      setDuplicateWarning(duplicate)
      return
    }

    const payload = buildWeightSampleCaptureCreatePayload({
      farmId,
      productionUnitId,
      lotId: selectedLotId,
      lot: selectedLot?.lotKey,
      weightGrams: weightValidation.value,
      sampleCount: sampleCountValidation.value,
      recordedAtIso,
      capturedByActorId: getCurrentOperatorActorId()
    })

    const result = await weightSampleLogApi.create(payload, { source: 'crewMode' })
    setExistingLogsForLot((prev) => [...prev, result.data])

    if (selectedLot) {
      void ensureTodayProductionRow({ farmId, productionUnitId }, { id: selectedLot.id, lotKey: selectedLot.lotKey }).catch(() => {})
    }

    recordCrewEvent('capture.confirm', {
      stream: 'weightSample',
      farmId,
      syncState: toSyncBadgeState(result.syncState),
      capturedByActorId: getCurrentOperatorActorId()
    })

    setLastCapture({
      weightSampleLogId: result.data.id,
      weightLabel: weightDraft.trim(),
      sampleCountLabel: sampleCountValidation.value !== undefined ? sampleCountDraft.trim() : null,
      state: toSyncBadgeState(result.syncState)
    })

    setWeightDraft('')
    setSampleCountDraft('')
    resetConfirmationState()

    result.syncPromise?.then(async () => {
      const status = await weightSampleLogApi.readWeightSampleLogSyncStatus([result.data.id])
      if (status.blockedCount > 0) {
        recordCrewEvent('capture.error', { stream: 'weightSample', farmId })
      }
      setLastCapture((prev) => (prev?.weightSampleLogId === result.data.id
        ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
        : prev))
    }).catch(() => {
      recordCrewEvent('capture.error', { stream: 'weightSample', farmId })
      setLastCapture((prev) => (prev?.weightSampleLogId === result.data.id ? { ...prev, state: 'error' } : prev))
    })
  }

  const handleAddIndividual = async () => {
    if (!farmId || !productionUnitId || !selectedLotId) return

    const weightValidation = validateWeightGramsValue(weightDraft)
    if (!weightValidation.ok) {
      setWeightError(
        weightValidation.reason === 'invalid'
          ? t('crewMode.weightSample.error.invalidWeight')
          : t('crewMode.weightSample.error.outOfRangeWeight', { max: String(WEIGHT_GRAMS_MAX) })
      )
      return
    }
    setWeightError(null)

    const lengthValidation = validateLengthMmValue(lengthDraft)
    if (!lengthValidation.ok) {
      setLengthError(
        lengthValidation.reason === 'invalid'
          ? t('crewMode.weightSample.error.invalidLength')
          : t('crewMode.weightSample.error.outOfRangeLength', { max: String(LENGTH_MM_MAX) })
      )
      return
    }
    setLengthError(null)

    const recordedAtIso = new Date().toISOString()
    // PRD §3 item 5 / Stage 2 finding 1: a lengthMm-bearing candidate warns
    // only on an exact weight+length match, not lotId+time alone — distinct
    // fish in the same round must not trip this on every reading.
    const duplicate = findLikelyDuplicateWeightSampleLog({
      lotId: selectedLotId,
      recordedAtIso,
      weightGrams: weightValidation.value,
      lengthMm: lengthValidation.value,
      existingLogs: existingLogsForLot,
      individual: true
    })
    if (duplicate && duplicateWarning?.id !== duplicate.id) {
      setDuplicateWarning(duplicate)
      return
    }

    // PRD §5: every individual reading is written with sampleCount fixed at
    // 1 — this is what lets the CV/K Reports dataset (PR 3/3) tell an
    // individual reading apart from a legacy mean-of-N row by value alone.
    const payload = buildWeightSampleCaptureCreatePayload({
      farmId,
      productionUnitId,
      lotId: selectedLotId,
      lot: selectedLot?.lotKey,
      weightGrams: weightValidation.value,
      lengthMm: lengthValidation.value,
      sampleCount: 1,
      recordedAtIso,
      capturedByActorId: getCurrentOperatorActorId()
    })

    const result = await weightSampleLogApi.create(payload, { source: 'crewMode' })
    setExistingLogsForLot((prev) => [...prev, result.data])

    if (selectedLot) {
      void ensureTodayProductionRow({ farmId, productionUnitId }, { id: selectedLot.id, lotKey: selectedLot.lotKey }).catch(() => {})
    }

    recordCrewEvent('capture.confirm', {
      stream: 'weightSample',
      farmId,
      syncState: toSyncBadgeState(result.syncState),
      capturedByActorId: getCurrentOperatorActorId()
    })

    const weightLabel = weightDraft.trim()
    const lengthLabel = lengthValidation.value !== undefined ? lengthDraft.trim() : null

    updateRound((prev) => [...prev, {
      weightSampleLogId: result.data.id,
      weightLabel,
      lengthLabel,
      recordedAt: recordedAtIso,
      state: toSyncBadgeState(result.syncState)
    }])

    setWeightDraft('')
    setLengthDraft('')
    resetConfirmationState()
    // Next fish: back to the weight box with no tap (§3 item 5).
    requestAnimationFrame(() => weightInputRef.current?.focus())

    result.syncPromise?.then(async () => {
      const status = await weightSampleLogApi.readWeightSampleLogSyncStatus([result.data.id])
      if (status.blockedCount > 0) {
        recordCrewEvent('capture.error', { stream: 'weightSample', farmId })
      }
      updateRound((prev) => prev.map((entry) => (entry.weightSampleLogId === result.data.id
        ? { ...entry, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
        : entry)))
    }).catch(() => {
      recordCrewEvent('capture.error', { stream: 'weightSample', farmId })
      updateRound((prev) => prev.map((entry) => (entry.weightSampleLogId === result.data.id ? { ...entry, state: 'error' } : entry)))
    })
  }

  // PRD §3 item 4: undoes only the single most-recently added row in the
  // active session — never an arbitrary past row. General retroactive edit
  // stays supervisor-only (unchanged non-goal).
  const handleUndoLast = async () => {
    const last = sessionEntries[sessionEntries.length - 1]
    if (!last) return
    // Findings #66 / user decision 2026-09-25: the operator's undo is an
    // audited delete with an automatic reason, so it costs no extra tap.
    await removeReading(last, t('crewCorrection.reason.operatorUndo'))
  }

  // Field Bundle v0 §3 item 7: any reading of the round can be removed, or
  // replaced (removed, its values put back in the boxes to fix and re-add).
  const removeReading = async (entry: SessionEntry, reason: string) => {
    await weightSampleLogApi.delete(entry.weightSampleLogId, buildOperatorUndoAudit(reason))
    updateRound((prev) => prev.filter((candidate) => candidate.weightSampleLogId !== entry.weightSampleLogId))
    setExistingLogsForLot((prev) => prev.filter((log) => log.id !== entry.weightSampleLogId))
    recordCrewEvent('capture.undo', { stream: 'weightSample', farmId })
  }

  const handleReplaceReading = async (entry: SessionEntry) => {
    await removeReading(entry, t('crewMode.weightSample.round.removedReason'))
    setWeightDraft(entry.weightLabel)
    setLengthDraft(entry.lengthLabel ?? '')
    requestAnimationFrame(() => weightInputRef.current?.focus())
  }

  const handleRetryFailedInSession = async () => {
    const failedIds = sessionEntries.filter((entry) => entry.state === 'error').map((entry) => entry.weightSampleLogId)
    if (failedIds.length === 0) return
    await weightSampleLogApi.retryWeightSampleLogSyncStatusWithReport(failedIds)
    await Promise.all(failedIds.map(async (id) => {
      const status = await weightSampleLogApi.readWeightSampleLogSyncStatus([id])
      updateRound((prev) => prev.map((entry) => (entry.weightSampleLogId === id
        ? { ...entry, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
        : entry)))
    }))
  }

  // PRD §3 item 4 / §9: "Finish round" clears the running tally and returns
  // to the pond/lot picker — for a single-active-lot pond this re-resolves
  // instantly (session setup, not "from zero"), matching §6.
  const handleFinishRound = () => {
    if (selectedLotId) clearStoredRound({ farmId, lotId: selectedLotId, operatorActorId })
    setSessionEntries([])
    setWeightDraft('')
    setLengthDraft('')
    resetConfirmationState()
  }

  const handleUndoMean = async () => {
    if (!lastCapture) return
    const id = lastCapture.weightSampleLogId
    await weightSampleLogApi.delete(id, buildOperatorUndoAudit(t('crewCorrection.reason.operatorUndo')))
    setExistingLogsForLot((prev) => prev.filter((log) => log.id !== id))
    setLastCapture(null)
    recordCrewEvent('capture.undo', { stream: 'weightSample', farmId })
  }

  const handleRetry = async () => {
    if (!lastCapture) return
    await weightSampleLogApi.retryWeightSampleLogSyncStatusWithReport([lastCapture.weightSampleLogId])
    const status = await weightSampleLogApi.readWeightSampleLogSyncStatus([lastCapture.weightSampleLogId])
    setLastCapture((prev) => (prev?.weightSampleLogId === lastCapture.weightSampleLogId
      ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : 'pending') }
      : prev))
  }

  // Findings #137: flip the badges (mean and round readings) once the
  // background flush sends them.
  const unsyncedRoundIds = sessionEntries.filter((entry) => isUnsyncedBadgeState(entry.state)).map((entry) => entry.weightSampleLogId)
  useRecheckWhileUnsynced(isUnsyncedBadgeState(lastCapture?.state) || unsyncedRoundIds.length > 0, async () => {
    if (lastCapture && isUnsyncedBadgeState(lastCapture.state)) {
      const id = lastCapture.weightSampleLogId
      const status = await weightSampleLogApi.readWeightSampleLogSyncStatus([id])
      setLastCapture((prev) => (prev?.weightSampleLogId === id
        ? { ...prev, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : prev.state) }
        : prev))
    }
    for (const id of unsyncedRoundIds) {
      // eslint-disable-next-line no-await-in-loop
      const status = await weightSampleLogApi.readWeightSampleLogSyncStatus([id])
      updateRound((prev) => prev.map((entry) => (entry.weightSampleLogId === id
        ? { ...entry, state: status.blockedCount > 0 ? 'error' : (status.syncState === 'synced' ? 'synced' : entry.state) }
        : entry)))
    }
  })

  if (farmId && !isWeightSampleCrewModeEnabled(farmId)) {
    return (
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />
        <div data-testid="weight-sample-capture-flag-disabled" style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>
          {t('crewMode.weightSample.notEnabledForFarm')}
        </div>
      </div>
    )
  }

  const sessionSyncedCount = sessionEntries.filter((entry) => entry.state === 'synced').length
  const sessionIssueCount = sessionEntries.length - sessionSyncedCount - sessionEntries.filter((entry) => entry.state === 'local').length

  return (
    <OperatorGate>
      <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <CrewModeFarmBar farmId={farmId} farms={farms} onSelectFarm={selectFarm} />

        <CrewLocationHeader stream="weightSample" location={location} onChange={crewLocation.openPicker} />
        <CrewLocationPicker
          stream="weightSample"
          open={crewLocation.pickerOpen}
          options={crewLocation.options}
          currentKey={location?.key}
          onChoose={(option) => {
            crewLocation.choose(option)
            setLastCapture(null)
          }}
          onClose={crewLocation.closePicker}
        />

        {selectedLotId && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 8, display: 'block' }}>
              {t('crewMode.weightSample.mode.label')}
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                data-testid="weight-sample-capture-mode-individual"
                onClick={() => { setMode('individual'); resetConfirmationState() }}
                style={modeButtonStyle(mode === 'individual')}
              >
                {t('crewMode.weightSample.mode.individual')}
              </button>
              <button
                type="button"
                data-testid="weight-sample-capture-mode-mean"
                onClick={() => { setMode('mean'); resetConfirmationState() }}
                style={modeButtonStyle(mode === 'mean')}
              >
                {t('crewMode.weightSample.mode.mean')}
              </button>
            </div>
          </div>
        )}

        {selectedLotId && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
              {t('crewMode.weightSample.weight')}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                ref={weightInputRef}
                data-testid="weight-sample-capture-weight-input"
                type="text"
                inputMode="decimal"
                enterKeyHint={mode === 'individual' && !showLength ? 'done' : 'next'}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return
                  event.preventDefault()
                  if (showLength) lengthInputRef.current?.focus()
                  else if (mode === 'individual') void handleAddIndividual()
                  else sampleCountInputRef.current?.focus()
                }}
                value={weightDraft}
                onChange={(event) => {
                  setWeightDraft(event.target.value)
                  resetConfirmationState()
                }}
                style={numericInputStyle(Boolean(weightError))}
              />
              <span data-testid="weight-sample-capture-weight-unit" style={{ fontSize: 18, fontWeight: 800, color: '#374151' }}>
                {t('crewMode.weightSample.weightUnit')}
              </span>
            </div>
            {weightError && (
              <div data-testid="weight-sample-capture-weight-error" style={{ marginTop: 6, color: '#dc2626', fontWeight: 700 }}>
                {weightError}
              </div>
            )}
          </div>
        )}

        {selectedLotId && showLength && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
              {t('crewMode.weightSample.length')}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                ref={lengthInputRef}
                data-testid="weight-sample-capture-length-input"
                type="text"
                inputMode="decimal"
                enterKeyHint="done"
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return
                  event.preventDefault()
                  void handleAddIndividual()
                }}
                value={lengthDraft}
                onChange={(event) => {
                  setLengthDraft(event.target.value)
                  resetConfirmationState()
                }}
                style={numericInputStyle(Boolean(lengthError))}
              />
              <span data-testid="weight-sample-capture-length-unit" style={{ fontSize: 18, fontWeight: 800, color: '#374151' }}>
                {t('crewMode.weightSample.lengthUnit')}
              </span>
            </div>
            {lengthError && (
              <div data-testid="weight-sample-capture-length-error" style={{ marginTop: 6, color: '#dc2626', fontWeight: 700 }}>
                {lengthError}
              </div>
            )}
          </div>
        )}

        {selectedLotId && mode === 'mean' && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>
              {t('crewMode.weightSample.sampleCount')}
            </label>
            <input
              ref={sampleCountInputRef}
              data-testid="weight-sample-capture-sample-count-input"
              type="text"
              inputMode="numeric"
              enterKeyHint="done"
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return
                event.preventDefault()
                void handleConfirmMean()
              }}
              value={sampleCountDraft}
              onChange={(event) => {
                setSampleCountDraft(event.target.value)
                resetConfirmationState()
              }}
              style={numericInputStyle(Boolean(sampleCountError))}
            />
            {sampleCountError && (
              <div data-testid="weight-sample-capture-sample-count-error" style={{ marginTop: 6, color: '#dc2626', fontWeight: 700 }}>
                {sampleCountError}
              </div>
            )}
          </div>
        )}

        {selectedLotId && duplicateWarning && (
          <div data-testid="weight-sample-capture-duplicate-warning" style={{ color: '#92400e', fontWeight: 700 }}>
            {t('crewMode.weightSample.warning.duplicate')}
          </div>
        )}

        {selectedLotId && mode === 'mean' && (
          <button
            type="button"
            data-testid="weight-sample-capture-confirm"
            onClick={() => void handleConfirmMean()}
            style={primaryButtonStyle}
          >
            {duplicateWarning ? t('crewMode.weightSample.confirmAnyway') : t('crewMode.weightSample.confirm')}
          </button>
        )}

        {selectedLotId && mode === 'individual' && samplingPlan && (
          <div
            data-testid="weight-sample-capture-plan-counter"
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              fontWeight: 800,
              fontSize: 18,
              textAlign: 'center',
              backgroundColor: sessionEntries.length >= samplingPlan.fishCount ? '#f0fdf4' : '#f3f4f6',
              color: sessionEntries.length >= samplingPlan.fishCount ? '#166534' : '#374151'
            }}
          >
            {t('crewMode.weightSample.plan.counter', {
              count: String(sessionEntries.length),
              total: String(samplingPlan.fishCount)
            })}
            {sessionEntries.length >= samplingPlan.fishCount && (
              <span data-testid="weight-sample-capture-plan-complete" style={{ display: 'block', fontSize: 14 }}>
                {t('crewMode.weightSample.plan.complete')}
              </span>
            )}
          </div>
        )}

        {selectedLotId && mode === 'individual' && (
          <>
            <button
              type="button"
              data-testid="weight-sample-capture-add"
              onClick={() => void handleAddIndividual()}
              style={addButtonStyle}
            >
              {duplicateWarning ? t('crewMode.weightSample.addAnyway') : t('crewMode.weightSample.add')}
            </button>

            {sessionEntries.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div data-testid="weight-sample-capture-session-count" style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                  {location ? `${location.label} · ` : ''}
                  {t('crewMode.weightSample.session.count', { count: String(sessionEntries.length) })}
                </div>
                <ol data-testid="weight-sample-capture-round-list" style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
                  {sessionEntries.map((entry, index) => (
                    <li
                      key={entry.weightSampleLogId}
                      data-testid="weight-sample-capture-round-reading"
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 10, ...syncBadgeStyle[entry.state] }}
                    >
                      {/* Findings #174: "1." next to "0.1" read as "1.01" — the fish number is a separate grey label. */}
                      <span data-testid="weight-sample-capture-round-number" style={{ minWidth: 52, fontSize: 13, fontWeight: 500, color: '#6b7280' }}>
                        {t('crewMode.weightSample.round.fishNumber', { n: String(index + 1) })}
                      </span>
                      <span style={{ flex: 1, fontWeight: 700 }}>
                        {entry.weightLabel} {t('crewMode.weightSample.weightUnit')}
                        {entry.lengthLabel ? ` · ${entry.lengthLabel} ${t('crewMode.weightSample.lengthUnit')}` : ''}
                      </span>
                      <button
                        type="button"
                        data-testid="weight-sample-capture-round-replace"
                        onClick={() => void handleReplaceReading(entry)}
                        style={{ ...secondaryButtonStyle, width: 'auto', minHeight: 44, padding: '4px 10px', fontSize: 12 }}
                      >
                        {t('crewMode.weightSample.round.replace')}
                      </button>
                      <button
                        type="button"
                        data-testid="weight-sample-capture-round-remove"
                        onClick={() => void removeReading(entry, t('crewMode.weightSample.round.removedReason'))}
                        style={{ ...secondaryButtonStyle, width: 'auto', minHeight: 44, padding: '4px 10px', fontSize: 12 }}
                      >
                        {t('crewMode.weightSample.round.remove')}
                      </button>
                    </li>
                  ))}
                </ol>
                <div data-testid="weight-sample-capture-session-sync-summary" style={{ fontSize: 13, color: '#6b7280' }}>
                  {sessionIssueCount > 0
                    ? t('crewMode.weightSample.session.syncSummaryWithIssues', {
                      synced: String(sessionSyncedCount),
                      total: String(sessionEntries.length),
                      issues: String(sessionIssueCount)
                    })
                    : t('crewMode.weightSample.session.syncSummary', {
                      synced: String(sessionSyncedCount),
                      total: String(sessionEntries.length)
                    })}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    data-testid="weight-sample-capture-undo"
                    onClick={() => void handleUndoLast()}
                    style={{ ...secondaryButtonStyle, flex: 1 }}
                  >
                    {t('crewMode.weightSample.undoLast')}
                  </button>
                  {sessionIssueCount > 0 && (
                    <button
                      type="button"
                      data-testid="weight-sample-capture-retry-session"
                      onClick={() => void handleRetryFailedInSession()}
                      style={{ ...secondaryButtonStyle, flex: 1 }}
                    >
                      {t('crewMode.weightSample.session.retryFailed')}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="weight-sample-capture-finish-round"
                  onClick={handleFinishRound}
                  style={finishButtonStyle}
                >
                  {t('crewMode.weightSample.finishRound')}
                </button>
              </div>
            )}
          </>
        )}

        {mode === 'mean' && lastCapture && (
          <div
            data-testid="weight-sample-capture-last-entry"
            style={{
              padding: 12,
              borderRadius: 10,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              ...syncBadgeStyle[lastCapture.state]
            }}
          >
            <span style={{ fontWeight: 700 }}>
              {location ? <span style={{ display: 'block', fontSize: 13 }}>{location.label}</span> : null}
              {t('crewMode.weightSample.lastEntry', { weight: lastCapture.weightLabel })}
              {lastCapture.sampleCountLabel
                ? ` · ${t('crewMode.weightSample.lastEntryCount', { count: lastCapture.sampleCountLabel })}`
                : ''}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                data-testid="weight-sample-capture-mean-undo"
                onClick={() => void handleUndoMean()}
                style={{ ...chipButtonStyle, minHeight: 44, minWidth: 44, fontSize: 13, padding: '4px 10px', width: 'auto' }}
              >
                {t('crewMode.undo')}
              </button>
              <span data-testid="weight-sample-capture-sync-state" style={{ fontWeight: 800 }}>
                {t(syncBadgeLabelKey[lastCapture.state])}
              </span>
              {lastCapture.state === 'error' && (
                <button
                  type="button"
                  data-testid="weight-sample-capture-retry"
                  onClick={() => void handleRetry()}
                  style={{ ...chipButtonStyle, minHeight: 40, minWidth: 40, fontSize: 12, padding: '4px 10px', width: 'auto' }}
                >
                  {t('crewMode.weightSample.retry')}
                </button>
              )}
            </span>
          </div>
        )}

        {(lastCapture || sessionEntries.length > 0) && (
          <CrewNextLocationButton
            next={crewLocation.next}
            onGo={(option) => {
              crewLocation.choose(option)
              setLastCapture(null)
            }}
          />
        )}
      </div>
    </OperatorGate>
  )
}
